/**
 * Named questions you can ask about an entity, each one a preset of the
 * Visualizer state that answers it (ONE-3516).
 *
 * An intent is CONFIGURATION, not new graph code. Every one resolves to the
 * controls that already exist — the relationship-group focus from ONE-3505 and
 * the tag/classification filters from ONE-3506 — so adding a question costs a
 * row in this table rather than a rendering path.
 *
 * The labels are the point. "Blast radius" and "Who uses this" are what an
 * analyst already calls these questions; `group=user&tag=Executive` is what the
 * graph needs and what nobody should have to assemble by hand. The acceptance
 * criterion is that the user is not required to configure the graph to answer a
 * common question, and a named preset is how that is met.
 */
import { PivotNodeKind } from './pivot.models';

export interface PivotIntent {
  /** Stable key, carried in the URL so an intent-opened view is shareable. */
  key: string;
  /** What the analyst calls it. Never mentions groups, filters or graph state. */
  label: string;
  /** One line on what it will show, for the control's title text. */
  description: string;
  /** The relationship group to narrow to, or null for the whole neighbourhood. */
  group: PivotNodeKind | null;
  /** Classifications to narrow by. Applied only where they apply (ONE-3506). */
  classifications?: string[];
}

/**
 * Intents per entity kind, most useful first.
 *
 * Deliberately two or three per kind. The value of a preset is that it is
 * chosen quickly; a menu long enough to need reading is the graph configuration
 * it was meant to replace, wearing different words.
 */
export const PIVOT_INTENTS: Partial<Record<PivotNodeKind, PivotIntent[]>> = {
  endpoint: [
    {
      key: 'blast-radius',
      label: 'Blast radius',
      description: 'Everything this endpoint reaches — the people on it and the software it runs',
      group: null,
    },
    {
      key: 'who-uses',
      label: 'Who uses this',
      description: 'The people observed on this endpoint',
      group: 'user',
    },
    {
      key: 'exposure',
      label: 'Vulnerability exposure',
      description: 'Open vulnerabilities on this endpoint, by severity',
      group: 'vulnerability',
    },
  ],
  user: [
    {
      key: 'blast-radius',
      label: 'Blast radius',
      description: 'Everything this account reaches',
      group: null,
    },
    {
      key: 'what-they-reach',
      label: 'Machines they use',
      description: 'Endpoints this account has been observed on',
      group: 'endpoint',
    },
    {
      key: 'privileged-reach',
      label: 'Privileged reach',
      description: 'Endpoints this account reaches that carry privileged access',
      group: 'endpoint',
      classifications: ['privileged_access'],
    },
  ],
  application: [
    {
      key: 'blast-radius',
      label: 'Blast radius',
      description: 'Everything this application reaches',
      group: null,
    },
    {
      key: 'where-installed',
      label: 'Where it is installed',
      description: 'Endpoints running this application',
      group: 'endpoint',
    },
  ],
  vulnerability: [
    {
      key: 'blast-radius',
      label: 'Blast radius',
      description: 'Every endpoint carrying this vulnerability, and the people on them',
      group: null,
    },
    {
      key: 'affected-hosts',
      label: 'Affected hosts',
      description: 'Endpoints carrying this vulnerability',
      group: 'endpoint',
    },
  ],
};

export function intentsFor(kind: PivotNodeKind): PivotIntent[] {
  return PIVOT_INTENTS[kind] ?? [];
}

/**
 * Query params that open the Visualizer already answering an intent.
 *
 * Shaped for `router.navigate`'s `queryParams`, and deliberately the same keys
 * the Visualizer already reads and writes (ONE-3506) — so an intent-opened view
 * is bookmarkable and shareable exactly like a hand-built one, with no second
 * vocabulary to keep in step.
 */
export function intentQueryParams(
  kind: PivotNodeKind,
  id: number | string,
  label: string | null,
  intent: PivotIntent,
  returnUrl?: string,
): Record<string, unknown> {
  return {
    kind,
    id,
    ...(label ? { label } : {}),
    group: intent.group ?? null,
    classification: intent.classifications?.length ? intent.classifications : null,
    // Cleared explicitly: an intent defines the whole view, so a tag left over
    // from a previous visit would silently narrow the answer it gives.
    tag: null,
    intent: intent.key,
    ...(returnUrl ? { return: returnUrl } : {}),
  };
}
